// React
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'

// Components
import { CartFlowPicker } from 'ui/components/Picker'
import { LocationPicker } from 'ui/components/Picker/LocationPicker'
import { FulfillmentType } from './FulfillmentType'
import BCartPicker from 'ui/blocks/Picker/types/cart'
import ReadOnlyModeDisabledComponent from 'store/components/ReadOnlyModeDisabledComponent'

// Libraries
import _debounce from 'lodash/debounce'

// Hooks
import { useSettings } from 'shared/utils/useSettings'
import { useOrm } from 'shared/utils/useOrm'

// Shared
import { featureEnabled, FeatureFlags } from 'back_office/v2/utils/utils'
import AvailabilityActions from 'store/v2/redux/actions/availability'
import promiseDebounce from 'shared/utils/promiseDebounce'
import { useTranslation } from 'react-i18next'

/**
 * CartPicker Component
 *
 * This component renders a CartFlowPicker with additional functionality for handling
 * availability fetching and duration changes.
 *
 * @param {Object} props - The component props
 * @param {string} props.mode - The mode of the picker ('freely', 'timeslot_duration', 'timeslot_fixed')
 * @param {Function} props.onChange - Callback function when selection changes
 * @param {string} props.format - The date format to use
 * @param {boolean} props.useTimes - Whether to use times in the picker
 * @param {Object} props.cart - The cart object
 * @param {Object} props.durations - Object of available durations
 * @param {Object} props.timeslots - Object of available timeslots
 * @param {Object} props.initialValues - Initial values for the picker
 * @param {boolean} props.useAMOrPM - Whether to use AM/PM format
 * @param {Object} props.minDate - The minimum selectable date
 * @param {boolean} [props.disabled] - Whether the picker is disabled
 * @param {Object} props.availabilities - Object of availability data
 * @param {boolean} props.availabilityLoading - Whether availability data is currently loading
 * @param {number} props.lineCount - The number of lines in the cart
 * @param {boolean} props.showAvailability - Whether to show availability
 * @param {Function} props.fetchAvailability - Function to fetch availability data
 *
 * @returns {React.ReactNode}
 */
const CartPicker = ({ cart, availabilities, ...props }) => {
  const [duration, setDuration] = useState(null)

  const locations = useOrm('locations', (locations) => {
    return locations.getAll({ archived_at: [null, undefined], location_type: 'rental' })
  })
  const intervalInMinutes = useSettings('store.time_increment', {
    default: 15,
    transformer: (value) => parseInt(value)
  })

  const { t } = useTranslation('user')

  const handleMonthChange = (options, date) => {
    // Fetch availability on month change if setting enabled
    if (!props.showAvailability || !props.lineCount) return

    try {
      props.fetchAvailability(cart, {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        type: options.type,
        starts_at: options.values?.starts_at,
        durationData: duration
      })
    } catch {
      throw new Error('fetch_availability_error')
    }
  }

  const debouncedHandleMonthChange = () => {
    if (window.testMode) {
      return handleMonthChange
    } else {
      return promiseDebounce(handleMonthChange, 500)
    }
  }

  const handleAvailabilityFetch = (options, day) => {
    let date

    day = options?.day || day

    if (day) {
      date = day.toDate?.().getDate() ?? day
    }

    if (!props.showAvailability || !props.lineCount) return

    try {
      props.fetchAvailability(cart, {
        type: options.type,
        day: date,
        starts_at: options.values?.starts_at,
        month: options.values?.starts_at ? new Date(options.values?.starts_at).getMonth() + 1 : undefined,
        year: options.values?.starts_at ? new Date(options.values?.starts_at).getFullYear() : undefined,
        durationData: duration,
        location_id: options.values?.start_location_id
      })
    } catch {
      throw new Error('fetch_availability_error')
    }
  }

  const handleChangeDuration = (_duration, durationData) => {
    setDuration(durationData)
  }

  return (
    <ReadOnlyModeDisabledComponent>
      <BCartPicker>
        {featureEnabled(FeatureFlags.DELIVERIES) && (
          <FulfillmentType cart={cart} fetchAvailability={handleAvailabilityFetch} />
        )}
        {!featureEnabled(FeatureFlags.DELIVERIES) && (
          <LocationPicker
            type="cart"
            locations={locations}
            WrapperComponent={BCartPicker.Section}
            label={t('user.store.start_and_stop')}
            startLocationLabel={t('user.store.start_location')}
            stopLocationLabel={t('user.store.stop_location')}
          />
        )}
        <CartFlowPicker
          {...props}
          availabilities={availabilities}
          onMonthChange={debouncedHandleMonthChange()}
          onOpen={handleAvailabilityFetch}
          intervalInMinutes={intervalInMinutes}
          onDurationChange={handleChangeDuration}
        />
      </BCartPicker>
    </ReadOnlyModeDisabledComponent>
  )
}

CartPicker.propTypes = {
  cart: PropTypes.object,
  availabilities: PropTypes.object,
  lineCount: PropTypes.number,
  showAvailability: PropTypes.bool,
  fetchAvailability: PropTypes.func
}

const mapStateToProps = (state, props) => {
  const cart = props.cart || state.orm.carts.getById(state.client.cartId)

  return {
    mode: state.settings.get('store.period_type'),
    useTimes: state.settings.get('store.use_times'),
    format: state.settings.get('dates.format'),
    useAMOrPM: state.settings.get('dates.use_am_pm'),
    minDate: state.client.firstTimeSelectable,
    cart,
    availabilities: state.orm.shop_availabilities.getAll(),
    showAvailability:
      state.settings.get('store.use_availability') && state.settings.get('store.show_cart_availability'),
    availabilityLoading: state.orm.pendingRequests.includes('SHOP_AVAILABILITIES_FETCH_CALENDAR_AVAILABILITY'),
    lineCount: cart?.lines.length
  }
}

const mapDispatchToProps = (_dispatch, _props) => {
  return {
    fetchAvailability: (cart, params) => {
      // No cart - can't check availability
      if (!cart?.created_at) return

      // Do not send moment objects in a request (it will serialise and cause a CORS error)
      if (params.starts_at?._isAMomentObject) {
        params.starts_at = params.starts_at.toDate()
      }

      const date = new Date(params?.type === 'start' ? cart.starts_at || Date() : cart.stops_at || Date())
      const filter = {
        cart_id: cart.id,
        year: params?.year || date.getFullYear(),
        month: params?.month || date.getMonth() + 1,
        day: params?.day,
        // Always set to start, since we want "simple, stateless" availability for now
        // copied from back_office/modules/orders/components/OrderPicker/hooks.js:78
        type: 'start',
        duration_period: params?.durationData?.data?.period,
        start_location_id: params?.location_id
      }

      if (params?.starts_at) {
        Object.assign(filter, { starts_at: params?.starts_at })
      }

      AvailabilityActions.fetchCalendarAvailability({
        filter,
        apiVersion: 3
      })
    }
  }
}

export default reduxConnect(mapStateToProps, mapDispatchToProps)(CartPicker)
